import { Global } from '@emotion/react'
import { MantineProvider } from '@mantine/core'
import './assets/style/tailwind.css'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useAtom, useAtomValue } from 'jotai'
import { DateTime } from 'luxon'
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7'
import { PostHogProvider } from 'posthog-js/react'
import { PropsWithChildren, Suspense, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { HttpProvider } from './api/HttpProvider'
import globalStyle from './assets/style/global'
import Auth0ProviderWithHistory from './auth/Auth0ProviderWithHistory'
import { mantineTheme } from '@pubstack/ui/style/mantine.config'
import { useUrlSync } from './hooks/useUrlSync'
import { Context, configurationState, contextState, loadingState } from './state'
import { routes } from './utils/router'
import { scopeBasename } from './utils/scope-path'

export const transactionId = Math.random().toString(36).substring(2, 9)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
      retry: false,
    },
  },
})

const PostHogProviderWrapper = ({ children }: PropsWithChildren) => {
  if (window.location.hostname === 'localhost') return <>{children}</>
  return (
    <PostHogProvider
      apiKey={'phc_jdFlMGykhi91rjl3bwXQVlPa92GaOOkh31qsbFQieoP'}
      options={{ api_host: 'https://eu.i.posthog.com', person_profiles: 'identified_only', capture_pageview: false, enable_recording_console_log: true, session_recording: { maskAllInputs: false } }}
    >
      {children}
    </PostHogProvider>
  )
}

function SyncApp() {
  const [context, setContext] = useAtom(contextState)
  const [queries] = useUrlSync()
  useEffect(() => {
    const newContext: Context = {
      filters: queries['ctx'] ? JSON.parse(decodeURIComponent(atob(queries['ctx']))) : {},
      timeRange: {
        ...context.timeRange,
        from: queries['from'] ? DateTime.fromISO(queries['from']) : DateTime.local().startOf('day'),
        to: queries['to'] ? DateTime.fromISO(queries['to']) : DateTime.local().endOf('day'),
      },
    }
    setContext(newContext)
  }, [])

  return null
}

function AppDataLoader() {
  useAtomValue(configurationState)

  return (
    <Auth0ProviderWithHistory>
      <MantineProvider theme={mantineTheme}>
        <NuqsAdapter>
          <HttpProvider>
            <QueryClientProvider client={queryClient}>
              <ReactQueryDevtools initialIsOpen={false} />
              <PostHogProviderWrapper>
                <RouterProvider router={createBrowserRouter(routes, { basename: scopeBasename(window.location.pathname) })} />
              </PostHogProviderWrapper>
            </QueryClientProvider>
          </HttpProvider>
          <Global styles={[globalStyle]} />
        </NuqsAdapter>
      </MantineProvider>
    </Auth0ProviderWithHistory>
  )
}

const BaseLoader = () => (
  <div className={'loader-parent'}>
    <img id={'main-loading'} src={'/picto_400x400.svg'} width={'80px'} height={'80px'} alt={'pubstack loading rotating logo'} />
    <img src={'/Pubstack_Logo.svg'} alt={'pustack name logo'} />
  </div>
)

const Loader = () => {
  const loading = useAtomValue(loadingState)
  return loading ? <BaseLoader /> : null
}

const root = createRoot(document.getElementById('root')!)
root.render(
  <Suspense fallback={<BaseLoader />}>
    <Loader />
    <SyncApp />
    <AppDataLoader />
  </Suspense>
)
